/* eslint-disable @next/next/no-img-element */
"use client";

import { useEffect, useRef } from "react";
import type { ReactNode } from "react";
import Script from "next/script";
import { GoogleTagManager } from "@next/third-parties/google";
import { NuqsAdapter } from "nuqs/adapters/next/app";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import { useAuth, useUser } from "@clerk/nextjs";

import { ErrorCapture } from "@/components/analytics/ErrorCapture";
import { CtaClickTracker } from "@/components/analytics/CtaClickTracker";
import { OAuthSignUpTracker } from "@/components/analytics/OAuthSignUpTracker";
import { setAnalyticsUser, setGaConsent } from "@/lib/analytics";
import { LightboxProvider } from "@/components/layout/LightboxProvider";
import { AuthModalProvider } from "@/components/auth/AuthModalContext";
import { AuthModal } from "@/components/auth/AuthModal";
import { ReviewPromptModal } from "@/components/dashboard/ReviewPromptModal";
import { MembershipProvider } from "@/lib/hooks/useMembership";
import { MySignupsProvider } from "@/lib/hooks/useMySignups";
import type { FeatureFlags } from "@/lib/feature-flags";
import { FeaturesContext } from "@/lib/hooks/useFeature";
import { useConsent } from "@/lib/hooks/useConsent";

type Props = {
  readonly features: FeatureFlags;
  readonly children: ReactNode;
};

/**
 * Nur in der Produktion aktiv.
 *
 * Staging und Previews liegen hinter Vercels SSO-Schutz: Requests auf
 * `/ingest/*` werden dort auf die Vercel-Login-Seite umgeleitet und landen
 * im Browser als HTTP 500. PostHog wiederholt jeden Event bis zu viermal,
 * wodurch die parallelen Verbindungen zur Domain belegt bleiben — andere
 * Requests warten dann in der Queue. Beim Login hing `setActiveSignIn`
 * dadurch nachweislich 17,7 s, was ein Rennen zweier Navigationen ausloeste.
 *
 * Nebeneffekt, ebenso erwuenscht: Staging-Events verfaelschen die
 * Produktionsdaten nicht mehr, solange beide denselben Key verwenden.
 */
const isProduction = process.env.NEXT_PUBLIC_VERCEL_ENV === "production";

const gaId = process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_ID;

/**
 * Eigene Initialisierung statt `<GoogleAnalytics>` aus @next/third-parties:
 * Dessen `config` nimmt keine Parameter, Previews brauchen aber
 * `debug_mode`, damit auch ihre Seitenaufrufe vom GA-Filter
 * "Entwickler-Traffic" erfasst werden — nicht nur die eigenen Events.
 *
 * Bewusst der gemeinsame `dataLayer` mit dd360, wie bisher: Ob dd360 die
 * GA-Eintraege dort liest, ist nicht geklaert.
 */
const gaInitScript = gaId
  ? `window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('js', new Date());
gtag('config', '${gaId}'${isProduction ? "" : ", { debug_mode: true }"});`
  : "";

if (typeof window !== "undefined" && !posthog.__loaded && isProduction) {
  const isBot = /bot|crawl|spider|slurp|facebookexternalhit|WhatsApp|LinkedInBot|Twitterbot|Googlebot|Bingbot|Bytespider|GPTBot/i.test(
    navigator.userAgent
  );

  if (!isBot) {
    posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY!, {
      api_host: "/ingest",
      ui_host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
      person_profiles: "always",
      capture_pageview: false,
      capture_pageleave: true,
    });
  }
}

export const Providers = ({ features, children }: Props) => {
  const { statistics, ready: consentReady } = useConsent();
  const { userId } = useAuth();
  const { user } = useUser();

  useEffect(() => {
    // Ausserhalb der Produktion wurde nie initialisiert — die Methoden
    // laufen dann ins Leere oder werfen.
    if (!posthog.__loaded) return;
    posthog.set_config({
      persistence: statistics ? "localStorage+cookie" : "memory",
      disable_persistence: !statistics,
    });
  }, [statistics]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const utmSource = params.get("utm_source");
    if (utmSource !== "email") return;
    const key = `dialetics_utm_tracked_${params.get("utm_campaign")}_${params.get("utm_content")}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
    fetch("/api/analytics/email-click", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        utm_source: utmSource,
        utm_medium: params.get("utm_medium"),
        utm_campaign: params.get("utm_campaign"),
        utm_content: params.get("utm_content"),
        url: window.location.pathname,
      }),
      keepalive: true,
    }).catch(() => {});
  }, []);

  const prevUserId = useRef(userId);

  useEffect(() => {
    if (prevUserId.current && !userId) {
      sessionStorage.clear();
      for (const key of Object.keys(localStorage)) {
        if (key.startsWith("dialetics_") || key.startsWith("voucher_expiry_")) {
          localStorage.removeItem(key);
        }
      }
      // Das Aufraeumen oben laeuft immer; nur PostHog selbst ist ausserhalb
      // der Produktion nicht initialisiert.
      if (posthog.__loaded) posthog.reset();
    }
    if (userId && posthog.__loaded) {
      posthog.identify(userId, {
        has_active_membership: user?.publicMetadata?.has_active_membership === true,
      });
    }
    setAnalyticsUser(userId ?? null);
    prevUserId.current = userId;
  }, [userId, user?.publicMetadata?.has_active_membership]);

  // Basic Consent Mode: GA bekommt Events nur bei Statistik-Zustimmung. Bis
  // Cookiebot geantwortet hat, puffert lib/analytics.ts sie kurz.
  useEffect(() => {
    if (!gaId) {
      setGaConsent("denied");
      return;
    }
    if (statistics) setGaConsent("granted");
    else setGaConsent(consentReady ? "denied" : "unknown");

    // Widerruf mitten in der Sitzung: gtag.js bleibt geladen und wuerde
    // ueber die Enhanced Measurement weiter Seitenaufrufe senden. Googles
    // offizieller Abschalter stoppt das; bei erneuter Zustimmung wieder an.
    (window as unknown as Record<string, unknown>)[`ga-disable-${gaId}`] = !statistics;
  }, [statistics, consentReady]);

  return (
    <FeaturesContext.Provider value={features}>
      <PostHogProvider client={posthog}>
        <NuqsAdapter>
          <AuthModalProvider>
          <MembershipProvider>
          <MySignupsProvider>
          <LightboxProvider>
            <ErrorCapture />
            <CtaClickTracker />
            <OAuthSignUpTracker />
            {children}
            <ReviewPromptModal />
            <AuthModal promo={{
              subline: "Ab April 2026",
              headline: "Die Dialetics Membership",
              introHtml: "<p>Wir freuen uns riesig, dass die Dialetics Membership startet. Das erwartet dich als Mitglied:</p>",
              benefits: [
                { _uid: "1", text: "Online-Sprechstunden mit Expert:innen wie Prof. Dr. Othmar Moser oder Dr. Ulrike Becker" },
                { _uid: "2", text: "TopicTalks zu allen Themen bezüglich Leben mit Diabetes" },
                { _uid: "3", text: "Exklusive Rabatte auf unsere Reisen" },
              ],
              outroHtml: "<p>Hört sich gut an? Dann erstelle deinen kostenlosen Account um dich auf die Warteliste für die Dialetics Membership zu setzen.</p>",
            }} />
            {statistics && (
              <>
                {gaId && (
                  <>
                    <Script
                      id="ga-init"
                      strategy="afterInteractive"
                      dangerouslySetInnerHTML={{ __html: gaInitScript }}
                    />
                    <Script
                      id="ga-gtag"
                      strategy="afterInteractive"
                      src={`https://www.googletagmanager.com/gtag/js?id=${gaId}`}
                    />
                  </>
                )}

                <GoogleTagManager gtmId="AW-16845234362" />

                {/* Google Tag Manager */}
                {/* <Script
                  id="gtag-external"
                  strategy="afterInteractive"
                  src="https://www.googletagmanager.com/gtag/js?id=AW-16845234362"
                />
                <Script
                  id="gtag-inline"
                  strategy="afterInteractive"
                  dangerouslySetInnerHTML={{
                    __html: `window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());
                gtag('config', 'AW-16845234362');`,
                  }}
                /> */}
              {/* Clarity Script */}
              <Script
                id="clarity-script"
                strategy="afterInteractive"
                dangerouslySetInnerHTML={{
                  __html: `(function(c,l,a,r,i,t,y){
              c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
              t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
              y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
            })(window, document, "clarity", "script", "qgfv408nn9");`,
                }}
              />

            </>
          )}
          </LightboxProvider>
          </MySignupsProvider>
          </MembershipProvider>
          </AuthModalProvider>
        </NuqsAdapter>
      </PostHogProvider>
    </FeaturesContext.Provider>
  );
};
